import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDay, venceText } from "@/lib/solicitudes";
import { BackLink } from "@/components/conecta/Bits";
import { MemberAvatar } from "@/components/red/MemberAvatar";
import { EstadoBadge } from "@/components/red/EstadoBadge";
import { OwnerActions } from "@/components/red/OwnerActions";
import { PresentationCard } from "@/components/red/PresentationCard";
import { ReferidoPrompt } from "@/components/red/ReferidoPrompt";

const Header = ({ s, m, sub }) => (
  <div className="c-card p-4 space-y-3">
    <div className="flex items-center gap-3">
      <MemberAvatar m={m} />
      <Link to={`/conecta/u/${m.id}`} className="flex-1 min-w-0">
        <p data-testid="solicitud-member-name" className="font-semibold truncate">{m.nombre_publico}</p>
        <p className="text-xs c-text-2">{sub}</p>
      </Link>
      <EstadoBadge estado={s.estado} />
    </div>
    <div className="text-sm space-y-0.5">
      <p data-testid="solicitud-need" className="font-semibold">{s.necesidad}</p>
      <p className="text-xs c-text-2">{s.servicio_nombre}{s.ciudad ? ` · ${s.ciudad}` : ""} · {fmtDay(s.created_at)}{s.estado === "pendiente" ? ` · ${venceText(s.vence_en)}` : ""}</p>
    </div>
  </div>
);

const SharedContact = ({ c }) => (
  <div data-testid="solicitud-shared-contact" className="c-card p-4 space-y-1.5">
    <p className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted">Contacto compartido</p>
    <p data-testid="shared-contact-name" className="font-semibold">{c.nombre}</p>
    {c.telefono && <a data-testid="shared-contact-phone" href={`tel:${c.telefono}`} className="text-sm flex items-center gap-1.5 c-accent"><Phone className="w-4 h-4" /> {c.telefono}</a>}
    {c.email && <a data-testid="shared-contact-email" href={`mailto:${c.email}`} className="text-sm flex items-center gap-1.5 c-accent"><Mail className="w-4 h-4" /> {c.email}</a>}
  </div>
);

const circleText = (s) => [s.circulo === 1 ? "1er círculo" : s.circulo === 2 ? "2do círculo" : null, s.via && `vía ${s.via.nombre_publico}`].filter(Boolean).join(" · ");

const Sent = ({ s }) => (
  <>
    <Header s={s} m={s.dueno} sub={circleText(s) || "Tu red"} />
    {s.contacto && <SharedContact c={s.contacto} />}
    {s.estado === "pendiente" && <p className="text-sm c-text-2 px-1">Te avisaremos cuando {s.dueno.nombre_publico} responda.</p>}
    {s.estado === "no_disponible" && <p data-testid="solicitud-no-disponible" className="c-card p-4 text-sm c-text-2">No disponible.</p>}
    {s.referido_id && <Link to={`/conecta/negocios/${s.referido_id}`} data-testid="referido-link" className="c-card p-4 block text-sm font-semibold c-accent">Ver en Negocios: Recibido de {s.dueno.nombre_publico} →</Link>}
  </>
);

const Received = ({ s, setS, reload }) => (
  <>
    <Header s={s} m={s.solicitante} sub={[s.solicitante.ciudad, circleText(s), s.avalado_por && `Avalado por ${s.avalado_por.nombre_publico}`].filter(Boolean).join(" · ")} />
    {s.estado === "pendiente" && <OwnerActions s={s} onChange={setS} />}
    {s.estado === "aceptada" && <><p data-testid="solicitud-shared-note" className="text-sm c-text-2 px-1">Compartiste {s.presentacion?.contacto_nombre} con {s.solicitante.nombre_publico}.</p><OwnerActions s={s} onChange={setS} /></>}
    {s.presentacion && s.estado === "presentada" && <PresentationCard p={s.presentacion} solicitante={s.solicitante.nombre_publico} />}
    {["aceptada", "presentada"].includes(s.estado) && <ReferidoPrompt s={s} onDone={reload} />}
    {s.estado === "rechazada" && <p data-testid="solicitud-rejected" className="c-card p-4 text-sm c-text-2">Rechazaste esta solicitud{s.motivo ? `: ${s.motivo}` : "."} {s.solicitante.nombre_publico} solo ve “No disponible”.</p>}
    {s.estado === "vencida" && <p className="c-card p-4 text-sm c-text-2">Venció sin respuesta.</p>}
  </>
);

export default function SolicitudDetailPage() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get(`/red/solicitudes/${id}`).then((r) => setS(r.data)).catch((e) => { setS(false); toast.error(errMsg(e)); }), [id]);
  useEffect(() => { load(); }, [load]);
  if (s === false) return <div className="space-y-3"><BackLink to="/conecta/solicitudes" label="Mis solicitudes" /><p className="c-card p-6 text-center c-text-2">Solicitud no encontrada.</p></div>;
  if (!s) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  return (
    <div data-testid="solicitud-detail-page" className="space-y-3">
      <BackLink to={`/conecta/solicitudes${s.rol === "solicitante" ? "?tab=enviadas" : ""}`} label="Mis solicitudes" />
      {s.rol === "solicitante" ? <Sent s={s} /> : <Received s={s} setS={setS} reload={load} />}
    </div>
  );
}
